import { GAME_DOCS_PATH, GAME_STORE_PATH, LoginFromGame } from "@app/app/routing/paths.ts";
import { AuthRoute } from "@app/shared/lib/AuthRoute.tsx";
import Error from "@app/shared/ui/Error";
import { useRequiredPortalConfigContext } from "@entities/game/model/useConfigContext.ts";
import { useFirebaseAuth } from "@features/authV2/lib/firebase.ts";
import { Center, Loader } from "@mantine/core";
import log from "loglevel";
import React, { Suspense, useEffect } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { LoginFromFxEmail } from "src/pages/LoginFromEmailPage";
import { LoginWithTempToken } from "src/pages/LoginWithTempTokenPage";

const HeroLayout = React.lazy(() => import("@widgets/HeroLayout/HeroLayout"));
const LoginFromGamePage = React.lazy(() => import("@pages/LoginFromGamePage"));
const HeroSmallLayout = React.lazy(() => import("@widgets/HeroLayout/HeroSmallLayout"));
const Landing = React.lazy(() => import("@app/pages/LandingPage/Landing.tsx"));
const DocsScreen = React.lazy(() => import("@app/pages/DocsPage"));
const StoreReturnScreen = React.lazy(() => import("@app/pages/StorePage/Components/StoreReturnScreen"));
const StoreContinueWithPI = React.lazy(() => import("@app/pages/StorePage/Components/StoreContinueWithPI"));
const MerchantPaymentCompleted = React.lazy(() => import("@app/pages/StorePage/Components/MerchantPaymentCompleted"));
const Profile = React.lazy(() => import("@app/pages/ProfilePage"));
const PaymentFormPageLazy = React.lazy(() => import("@app/pages/PaymentFormPage/PaymentFormPage.tsx"));
const WebPlayer = React.lazy(() => import("@app/pages/WebPlayerPage/WebPlayer.tsx"));
const NewStoreScreen = React.lazy(() => import("@app/pages/StorePage/new"));
const DebugPage = React.lazy(() => import("@app/pages/DebugPage"));

/**
 * @fileoverview Роуты для режима "основной портал".
 */

/**
 * @description Компонент-заглушка для полноэкранных лениво загружаемых компонентов.
 */
const FullscreenFallback = function () {
	return (
		<Center h="100vh">
			<Loader size="xl" />
		</Center>
	);
};

/**
 * @description Компонент-заглушка для небольших лениво загружаемых компонентов внутри лэйаута.
 */
const SmallFallback = () => (
	<Center p="xl">
		<Loader />
	</Center>
);

const MainRoutes = () => {
	const { portalConfig } = useRequiredPortalConfigContext();
	const firebaseConfig = portalConfig!.PortalPublicWebConfig.CompanyBranding.FirebaseOptions;
	useFirebaseAuth(firebaseConfig);

	useEffect(() => {
		log.info("Using main portal routes");
	}, []);

	return (
		<Routes>
			<Route
				element={
					<Suspense fallback={<FullscreenFallback />}>
						<Outlet />
					</Suspense>
				}
			>
				<Route path="/play">
					<Route path=":game" element={<WebPlayer singleGameSite={false} />} />
					<Route path=":game/iframe/:adapter" element={<WebPlayer singleGameSite={false} />} />
				</Route>
				<Route path="/player">
					<Route path=":game" element={<WebPlayer singleGameSite={false} />} />
					<Route path=":game/iframe/:adapter" element={<WebPlayer singleGameSite={false} />} />
				</Route>

				{/* Роуты для новой верстки */}
				<Route path="/LoginWithTempToken" element={<LoginWithTempToken />} />
				<Route path="/Store/ContinueWithPI" element={<HeroSmallLayout portalConfig={portalConfig} />}>
					<Route index element={<StoreContinueWithPI />} />
				</Route>
				<Route path="Store/Return/:id" element={<StoreReturnScreen />} />
				<Route path="/Store/MerchantPaymentCompleted/:token" element={<MerchantPaymentCompleted />} />

				{/* Документация */}
				<Route path="/Docs/:doc" element={<DocsScreen portalConfig={portalConfig} />} />
				<Route path={GAME_DOCS_PATH} element={<DocsScreen portalConfig={portalConfig} />} />

				{/* Платежная форма */}
				<Route path="/Store/Bank131/PaymentForm" element={<PaymentFormPageLazy />} />
			</Route>

			{/* Основной лэйаут портала */}
			<Route
				path="/"
				element={
					<Suspense fallback={<FullscreenFallback />}>
						<HeroLayout portalConfig={portalConfig} />
					</Suspense>
				}
			>
				<Route
					element={
						<Suspense fallback={<SmallFallback />}>
							<Outlet />
						</Suspense>
					}
				>
					<Route index element={<Landing />} />
					<Route path={GAME_STORE_PATH} element={<NewStoreScreen />} />
					<Route path="LoginValidateEmail" element={<LoginFromFxEmail />} />
					<Route path={LoginFromGame} element={<LoginFromGamePage />} />
					<Route path="Error" element={<Error />} />
					<Route
						path="Profile"
						element={
							<AuthRoute path="/Profile">
								<Profile />
							</AuthRoute>
						}
					/>
					{import.meta.env.DEV && <Route path="/debug" element={<DebugPage />} />}
				</Route>
			</Route>
			<Route path="*" element={<Navigate to="/" replace />} />
		</Routes>
	);
};

export default MainRoutes;
